import React, { useMemo, useState, useEffect } from 'react';
import { CalendarDays, Send } from 'lucide-react';
import { api } from '../config/api';
import toast from 'react-hot-toast';
import { formatDisplayDate } from '../utils/dateFormat';
import PaginationControls from '../components/PaginationControls';
import {
  masterHeader, masterPageShell, masterTitle, masterTableData
} from '../components/masterPageStyles';

const AppointmentPatientsUpdate = () => {
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [filterDoctor, setFilterDoctor] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const [bulkAction, setBulkAction] = useState('');
  const [param3, setParam3] = useState('');
  const [param4, setParam4] = useState('');

  const loadData = async () => {
    try {
      const [apptRes, docRes] = await Promise.all([api.get(`/appointments/all`), api.get(`/doctors/all`)]);
      if (apptRes.data.success) setAppointments(apptRes.data.data || []);
      if (docRes.data.success) setDoctors(docRes.data.data || []);
    } catch (err) { toast.error('FAILED TO LOAD DATA'); }
  };

  useEffect(() => { loadData(); }, []);

const filteredAppointments = useMemo(() => {
    return appointments.filter((item) => {
      // Add this line to only show 'Scheduled'
      const matchesStatus = item.status === 'Scheduled'; 
      
      const matchesDate = filterDate ? item.appointmentDate === filterDate : true;
      const matchesDoctor = filterDoctor ? item.doctorId === filterDoctor : true;
      
      return matchesStatus && matchesDate && matchesDoctor;
    });
  }, [appointments, filterDate, filterDoctor]);

const formatStartOnly12Hr = (timeStr) => {
  if (!timeStr) return '';
  let raw = String(timeStr).split('-')[0].trim();
  if (raw.toUpperCase().includes('AM') || raw.toUpperCase().includes('PM')) return raw.toUpperCase();
  const clean = raw.replace('.', ':');
  const parts = clean.split(':').map(Number);
  if (isNaN(parts[0])) return raw;
  let hours = parts[0];
  const minutes = parts[1] !== undefined && !isNaN(parts[1]) ? parts[1] : 0;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

const handleBulkSend = async () => {
    if (!bulkAction) return toast.error("SELECT ACTION TYPE");
    if (!window.confirm(`SEND TO ${filteredAppointments.length} PATIENTS?`)) return;

    const templateMap = {
      late: 'doctors_late',        
      absent: 'doctors_absents',    
      reschedule: 'doctors_reschedule', 
      early: 'doctors_early'      
    };

    for (const appt of filteredAppointments) {
      const dateParts = (appt.appointmentDate || '').split('-');
      const formattedApptDate = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : appt.appointmentDate;

      const formattedTime3 = formatStartOnly12Hr(param3);
      const formattedTime4 = formatStartOnly12Hr(param4);

      // 1. Send WhatsApp Call
      try {
        let params = [appt.patientName, appt.doctorName];
        
        if (bulkAction === 'late' || bulkAction === 'early') {
           params.push(formattedTime3); // e.g. 05:00 PM
        }
        if (bulkAction === 'reschedule') {
           params.push(param3, formattedTime4); // param3 = Date, param4 = 05:00 PM
        }
        
        await api.post('/appointments/send-whatsapp', {
          destination: appt.patientMobile,
          campaignName: templateMap[bulkAction],
          templateParams: params
        });
      } catch (err) { 
        console.error('WhatsApp Failed for:', appt.patientName, err.message); 
      }

      // 2. Send Offline SMS Call
      try {
        await api.post('/appointments/send-bulk-sms', {
          mobile: appt.patientMobile,
          actionType: bulkAction,
          patientName: appt.patientName,
          doctorName: appt.doctorName,
          date: bulkAction === 'reschedule' ? param3 : formattedApptDate,
          time: bulkAction === 'reschedule' ? formattedTime4 : formattedTime3
        });
      } catch (err) {
        console.error('Offline SMS Failed for:', appt.patientName, err.message);
      }
    }

    toast.success('BULK UPDATE SENT');
};

  return (
    <div className={masterPageShell}>
      {/* 1. Header */}
      <header className={`${masterHeader} p-1`}>
        <div className="flex items-center gap-2">
          <div className="p-1 bg-emerald-100 text-emerald-600 rounded-lg"><CalendarDays size={14} /></div>
          <h1 className={masterTitle}>Appointment Bulk Notify</h1>
        </div>
      </header>

      <main className="px-1">
        <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-300 shadow-sm mt-1">
          {/* 2. Controls Bar (Only Filters + Action) */}
          <div className="flex flex-wrap items-center gap-2 p-1 bg-slate-50 border-b">
            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="border px-1 text-[11px] font-bold h-[30px]" />
            <select value={filterDoctor} onChange={(e) => setFilterDoctor(e.target.value)} className="border px-1 text-[11px] font-bold h-[30px]"><option value="">-- ALL DOCTORS --</option>{doctors.map(d => <option key={d.doctorId} value={d.doctorId}>{d.doctorName}</option>)}</select>
            
            <div className="h-5 w-[1px] bg-slate-300 mx-1" />

           {/* New Bulk Action Header with Placeholder Clarity */}
        <div className="flex items-center gap-2 p-1 bg-emerald-50 border-b">
          <select className="border border-emerald-600 p-1 text-[10px] font-bold h-[30px] rounded" onChange={(e) => setBulkAction(e.target.value)}>
            <option value="">-- SELECT ACTION --</option>
            <option value="late">DOCTOR LATE</option>
            <option value="absent">DOCTOR ABSENT</option>
            <option value="reschedule">RESCHEDULE</option>
            <option value="early">DOCTOR EARLY</option>
          </select>
          
          {(bulkAction === 'late' || bulkAction === 'early') && (
            <input 
              placeholder="10:05-10:10" 
              className="border p-1 text-[10px] h-[30px] rounded w-48" 
              onChange={(e) => setParam3(e.target.value)} 
            />
          )}
          
          {bulkAction === 'reschedule' && (
            <>
              <input 
                placeholder="19/06/2026" 
                className="border p-1 text-[10px] h-[30px] rounded w-32" 
                onChange={(e) => setParam3(e.target.value)} 
              />
              <input 
                placeholder="10:05-10:10" 
                className="border p-1 text-[10px] h-[30px] rounded w-32" 
                onChange={(e) => setParam4(e.target.value)} 
              />
            </>
          )}
          
          <button 
            onClick={handleBulkSend} 
            className="bg-emerald-600 text-white px-3 h-[30px] rounded text-[10px] font-bold flex items-center gap-1 ml-auto"
          >
            <Send size={10} /> SEND BULK
          </button>
        </div>
          </div>

          {/* 3. Table */}
          <div className="max-h-[calc(100vh-220px)] overflow-auto">
            <table className="w-full min-w-[1000px] table-fixed text-left text-[10px]">
              <thead className="sticky top-0 bg-slate-100 text-[9px] font-extrabold uppercase text-emerald-600 border-b">
                <tr>
                  <th className={`${masterTableData} w-[8%]`}>ID</th>
                  <th className={masterTableData}>Patient</th>
                  <th className={masterTableData}>Mobile</th>
                  <th className={masterTableData}>Doctor</th>
                  <th className={masterTableData}>Date</th>
                  <th className={masterTableData}>Slot Time</th>
                  <th className={masterTableData}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y font-bold text-slate-700">
                {(pageSize === -1 ? filteredAppointments : filteredAppointments.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)).map((item) => (
                  <tr key={item._id} className="hover:bg-emerald-50">
                    <td className={masterTableData}>{item.appointmentId}</td>
                    <td className={masterTableData}>{item.patientName}</td>
                    <td className={masterTableData}>{item.patientMobile}</td>
                    <td className={masterTableData}>{item.doctorName}</td>
                    <td className={masterTableData}>{formatDisplayDate(item.appointmentDate)}</td>
                    <td className={masterTableData}>{item.selectedSlot}</td>
                    <td className={masterTableData}>{item.status || 'Scheduled'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* 4. Pagination at the Bottom */}
          <div className="p-2 border-t">
            <PaginationControls totalItems={filteredAppointments.length} currentPage={currentPage} pageSize={pageSize} onPageChange={setCurrentPage} onPageSizeChange={setPageSize} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AppointmentPatientsUpdate;